"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, ShieldCheck, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const advantages = [{
  title: "Trusted Expertise",
  description: "Our team of legal professionals specializes in navigating complex timeshare laws, providing the high-trust representation you need to break free.",
  icon: ShieldCheck
}, {
  title: "Personalized Guidance",
  description: "We understand every situation is unique. Our experts tailor a specific exit strategy to your resort, mortgage status, and contract terms.",
  icon: MapPin
}, {
  title: "Ethical Solutions",
  description: "No high-pressure tactics. We focus on transparent communication and reliable, ethical results to help you regain your financial peace of mind.",
  icon: TrendingUp
}];
export default function TheBimbolaAdvantage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="98310" id="the-nexus-advantage" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.02] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1627436/img/adire_pattern_watermark.png)] bg-repeat pointer-events-none mix-blend-multiply" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary mb-6 tracking-tight border-b-4 border-secondary pb-4 inline-block">
            The Nexus Advantage
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed mt-4">
            Experience the gold standard in timeshare exit services. We provide the legal depth and dedicated support required to navigate your path away from the burden of rising fees.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {advantages.map((adv, i) => <div key={i} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="h-full bg-card backdrop-blur-md border-border/60 shadow-lg hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-300 group rounded-lg">
                <CardHeader className="pb-4">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl bg-secondary/10 group-hover:bg-secondary/20 transition-colors duration-300">
                    {}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-secondary/20 border border-secondary/30" />
                    <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-secondary/40" />
                    
                    {}
                    <adv.icon className="w-8 h-8 text-secondary relative z-10" />
                  </div>
                  <CardTitle className="text-2xl font-default font-bold text-primary">
                    {adv.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-default leading-relaxed text-base">
                    {adv.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}